import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * CacheCommonUsagePanel — §6.7 공통 모듈 시연 패널
 * C051 (Caffeine) + C050 (Redis) + C014 (Audit) + C026 (VT) + C030 (@HasPermission) 활용 현황
 */
export function CacheCommonUsagePanel() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const usages = [
    {
      module: '★ eraf-data-cache (C051)',
      api: 'CacheStatsService.all()',
      role: 'L1 캐시 통계 수집 (hitRate/estimatedSize)',
      snippet: 'cacheStatsService.all()',
    },
    {
      module: '★ eraf-data-cache (C051)',
      api: 'CacheStatsService.evict(name)',
      role: 'L1 캐시 evict 핵심 (미존재 시 CACHE-404-001)',
      snippet: 'cacheStatsService.evict("commonCodeGroups")',
    },
    {
      module: '★ eraf-data-cache (C051)',
      api: 'CacheManager.getCacheNames()',
      role: '캐시명 열거 · Caffeine native asMap 접근',
      snippet: 'erafCacheManager.getCacheNames()',
    },
    {
      module: '★ eraf-data-redis (C050)',
      api: 'StringRedisTemplate SCAN MATCH',
      role: 'L2 keyspace 비차단 cursor 스캔 (KEYS 금지)',
      snippet: 'redis.scan(ScanOptions.scanOptions().match(pattern).count(200).build())',
    },
    {
      module: '★ eraf-data-redis (C050)',
      api: 'RedisKeyspace.of("cache", name)',
      role: 'L2 키 prefix 조립 (eraf:cache:<name>:*)',
      snippet: 'RedisKeyspace.of("cache", "rbac-permissions") + ":*"',
    },
    {
      module: 'eraf-data-redis (C050)',
      api: 'RedisSupportProperties.Cache.isEnabled()',
      role: 'L2 활성 판별 → degrade-graceful (DISABLED/UNAVAILABLE)',
      snippet: 'redisProps.getCache().isEnabled()',
    },
    {
      module: '★ eraf-core (C026)',
      api: '@Async + spring.threads.virtual.enabled',
      role: 'L2 SCAN 을 VT 에서 비차단 실행 (traceId 보존)',
      snippet: '@Async public CompletableFuture<...> scanL2(...)',
    },
    {
      module: '★ eraf-core (C014)',
      api: '@Audited(action="CACHE_EVICTED")',
      role: '모든 evict 작업 자동 감사 기록 (actor/IP/result)',
      snippet: '@Audited(action="CACHE_EVICTED", resource="cache", resourceIdExpression="#name")',
    },
    {
      module: 'eraf-core (C002)',
      api: 'ApiResponse.success(data)',
      role: '표준 응답 래핑',
      snippet: 'return ApiResponse.success(service.listCaches(tier, keyword))',
    },
    {
      module: 'eraf-security (C030)',
      api: '@HasPermission("CACHE_EVICT")',
      role: 'evict 권한 (이중 방어 — Security Chain + AOP)',
      snippet: '@HasPermission("CACHE_EVICT") public EvictResultResponse evict(...)',
    },
    {
      module: 'eraf-security (C029)',
      api: 'SecurityChainCustomizer + PathPolicy',
      role: '/cache-admin/** ROLE_ADMIN/ROLE_EXEC 1차 게이트',
      snippet: 'PathPolicy.hasRole("/api/eraf/cache-admin", "ADMIN")',
    },
    {
      module: 'eraf-security (C031)',
      api: 'SecurityAuditLogger',
      role: '권한 거부 자동 보안 감사 (biz 코드 0줄)',
      snippet: '(자동)',
    },
  ];

  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        <span>▾ {t('cacheAdmin.commonUsage.title')}</span>
        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400 }}>
          {t('cacheAdmin.commonUsage.description')}
        </span>
      </button>

      {expanded && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {(
                  [
                    'cacheAdmin.commonUsage.col.module',
                    'cacheAdmin.commonUsage.col.api',
                    'cacheAdmin.commonUsage.col.role',
                    'cacheAdmin.commonUsage.col.snippet',
                  ] as const
                ).map((key) => (
                  <th
                    key={key}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usages.map((u, i) => (
                <tr
                  key={i}
                  style={{ borderBottom: '1px solid var(--color-surface-border)' }}
                >
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      fontWeight: u.module.startsWith('★') ? 700 : 400,
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {u.module}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {u.api}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {u.role}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-muted)',
                      maxWidth: 260,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {u.snippet}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
